import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Clock, ExternalLink, Percent } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { useToggleEnabled } from './useToggleEnabled'
import { Stat, Switch } from './ui'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'

const LIST_KEY = ['content', 'conventionConfiguration', 'all'] as const

/** Discount is stored as free text ("10%", "20", "−15%"). Normalise to "−N%". */
function formatDiscount(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s) return '—'
  const m = s.match(/(\d+(?:[.,]\d+)?)/)
  if (!m) return s
  return `−${m[1].replace(',', '.')}%`
}

/** validity.hoursFrom / hoursTo → "HH:MM" (tolerant of "", "8", "08:00"). */
function fmtHour(v: unknown): string {
  const s = String(v ?? '').trim()
  if (!s) return ''
  const m = s.match(/(\d{1,2}):(\d{2})/)
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`
  const n = Number(s)
  if (Number.isFinite(n)) return `${String(Math.floor(n)).padStart(2, '0')}:00`
  return s
}
function fascia(validity: unknown): string {
  const v = (validity ?? {}) as El
  const from = fmtHour(v.hoursFrom)
  const to = fmtHour(v.hoursTo)
  if (!from && !to) return 'Tutto il giorno'
  return `${from || '00:00'} – ${to || '24:00'}`
}

type Band = { id: string; name: string; discount: string; band: string; enabled: boolean }

export function ConventionConfigPage() {
  const [busyId, setBusyId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => getContentList('conventionConfiguration', { limit: 200 }),
  })
  const toggle = useToggleEnabled('conventionConfiguration', LIST_KEY)

  const bands = useMemo<Band[]>(() => {
    const rows = list.data?.data ?? []
    return rows
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? 'Senza nome'),
          discount: formatDiscount(el.discount),
          band: fascia(el.validity),
          enabled: isYes(el.enabled),
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const stats = useMemo(() => {
    const active = bands.filter((b) => b.enabled).length
    return { total: bands.length, active, inactive: bands.length - active }
  }, [bands])

  function flip(b: Band) {
    setBusyId(b.id)
    toggle.mutate({ id: b.id, enabled: !b.enabled }, { onSettled: () => setBusyId(null) })
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
            Scontistica convenzioni
          </h2>
          <p className="mt-1.5 max-w-[70ch] text-[13.5px] text-muted">
            Fasce di sconto applicate agli hotel convenzionati. Ogni fascia vale per una finestra oraria; attivala o
            disattivala da qui. Percentuale e orari si modificano nel <span className="text-coal">Gestionale</span>.
          </p>
        </div>
        <a
          href="/admin/yep-admin/content/conventionConfiguration/list"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint sm:ml-auto"
        >
          <ExternalLink className="h-4 w-4" /> Gestionale
        </a>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-3 gap-[14px]">
        <Stat label="Fasce sconto" value={String(stats.total)} sub="regole configurate" />
        <Stat label="Attive" value={String(stats.active)} sub="in applicazione" />
        <Stat label="Disattive" value={String(stats.inactive)} sub="sospese" />
      </div>

      {/* table */}
      <div className="cv-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-line-soft text-[11px] font-bold uppercase tracking-[1px] text-muted">
                <th className="px-4 py-3 text-left">Fascia sconto</th>
                <th className="px-4 py-3 text-right">Sconto</th>
                <th className="px-4 py-3 text-left">Finestra oraria</th>
                <th className="px-4 py-3 text-left">Stato</th>
                <th className="px-4 py-3 text-right">Attiva</th>
              </tr>
            </thead>
            <tbody>
              {list.isError ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center text-sm text-muted">
                    <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare le fasce sconto.
                  </td>
                </tr>
              ) : list.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-line-soft last:border-0">
                    {Array.from({ length: 5 }).map((_c, j) => (
                      <td key={j} className="px-4 py-3.5">
                        <div className="skeleton h-3.5 w-24 rounded-sm bg-line-soft" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : bands.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center text-sm text-muted">
                    Nessuna fascia sconto configurata.
                  </td>
                </tr>
              ) : (
                bands.map((b) => (
                  <tr
                    key={b.id}
                    className={cn(
                      'border-b border-line-soft last:border-0 hover:bg-paper',
                      !b.enabled && 'opacity-60',
                    )}
                  >
                    <td className="px-4 py-3 font-semibold text-coal">{b.name}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 rounded-md bg-verdigris/10 px-2 py-0.5 font-mono text-[12.5px] font-bold text-verdigris">
                        {b.discount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-faint" /> {b.band}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11.5px] font-bold',
                          b.enabled ? 'bg-ok-soft text-ok' : 'bg-line-soft text-muted',
                        )}
                      >
                        <span className={cn('h-[7px] w-[7px] rounded-full', b.enabled ? 'bg-ok' : 'bg-faint')} />
                        {b.enabled ? 'Attiva' : 'Disattiva'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <Switch on={b.enabled} busy={busyId === b.id} onClick={() => flip(b)} label={`Attiva ${b.name}`} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* note */}
      <div className="mt-[18px] flex items-start gap-3 rounded-card border border-[#f0e0b0] bg-oro-soft px-4 py-3.5 text-[13px] text-oro-700">
        <Percent className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          La fascia sconto si applica quando l’orario della prenotazione ricade nella sua finestra. Gli hotel a cui
          assegnarla si gestiscono in <b>Convenzioni → Hotel convenzionati</b>.
        </div>
      </div>
    </div>
  )
}
